﻿import { Dirs } from "@app/helpers/dirs";
import { ApiService } from "@app/services/api.service";
import { DialogService } from "@app/services/dialog.service";
import { RuleService } from "@app/services/rule.service";
import { Configurator, eAlertType, eAutoCompleteSource, eOptionFilterSource, eQueryMode, eSelectSource, eSingleOptionBehavior, eSort, Field, IConfiguratorRuleArgs, IJsResult, IOption, IOptionFilterField, IOptionFilterRuleArgs, IOptionFilterRunArgs, IRootScope, ISceneRuleArgs, ITable, ITableObject, Option, OptionFilter, RuleContainer, Table } from "@models";
import { KPromise } from "@rules";
import { Logger, SearchUtils, Utils } from "@tools";

export interface IRuleErrorDialogScope extends ng.IScope {
    ruleName?: string;
    error?: string;
    configuratorName?: string;
}

export interface IConfiguratorHelperArgs {
    $http: ng.IHttpService;
    $q: ng.IQService;
    api: ApiService;
    dialogService: DialogService;
    ruleService: RuleService;
    tracker: ng.IPromiseTracker;
    $rootScope: IRootScope;
    getRuleArgs: (config: Configurator, changedField?: Field) => IConfiguratorRuleArgs;
    showErrors: () => boolean;
}

/**
 * info class for temporary storage of select table data
 */
export interface IOptionFilterInfo {
    filterId: string;
    fieldIds: string[];
    // tablePromise?: ng.IPromise<Table>;
}

/**
 * NOT meant to be used as an angular singleton service.  It's a helper for running configurators
 */
export class ConfiguratorHelper {
    constructor(public args: IConfiguratorHelperArgs) {

    }

    /** holds info about the option filters, in a structure that orders them by dependency */
    public odb: { [idProduct: number]: { [id: string]: IOptionFilterInfo } };
    public filterLevelDb: { [idProduct: number]: { [level: number]: string[] } } = {};
    /** once the tablespromise resolves, will be filled with the tables converted to arrays 
     * (for sending in to the rule args) 
     */
    public tableArraysDb: { [id: number]: ITableObject } = {};
    public allOptionFilterRules: Function;

    public downloadTables(tableIds: number[]): ng.IPromise<any> {
        if (tableIds.length) {
            return this.args.api.tables.getBatch({ ids: tableIds }, this.args.tracker).then(data => {
                this.loadTables(data);
            });
        } else {
            return this.args.$q.when(null);
        }
    }

    public loadTables(itables: ITable[]) {
        itables.forEach(t => {
            let table = new Table(t);
            this.tableArraysDb[table.id] = table.toObjectArray();
        });
    }

    public handleRuleError(
        ruleName: string,
        result: IJsResult<IConfiguratorRuleArgs | ISceneRuleArgs>
    ) {
        if (result && result.hasError && this.args.showErrors()) {
            let dialogScope: IRuleErrorDialogScope = this.args.$rootScope.$new();
            dialogScope.ruleName = ruleName;
            dialogScope.configuratorName = result.parameters.configurator.name;
            dialogScope.error = result.error.message;
            this.args.dialogService.alert({
                type: eAlertType.error,
                template: Dirs.view("alert-rule-error"),
                scope: dialogScope,
                persist: true
            });
        }
    }

    public runRuleContainerAsync<T extends IConfiguratorRuleArgs | ISceneRuleArgs>(
        ruleContainer: RuleContainer,
        args?: T
    ): ng.IPromise<IJsResult<T>> {
        console.log('run rule async', ruleContainer);
        return this.args.ruleService.runRuleContainerAsync(ruleContainer, args || this.args.getRuleArgs(ruleContainer.$parentConfigurator) as T).then(res => {
            this.handleRuleError(ruleContainer.name + " " + ruleContainer.$type, res);
            return res;
        });
    }

    public runRuleContainerSync<T extends IConfiguratorRuleArgs | ISceneRuleArgs>(
        ruleContainer: RuleContainer,
        args?: T
    ): IJsResult<T> {
        let res = this.args.ruleService.runRuleContainerSync(ruleContainer, args || this.args.getRuleArgs(ruleContainer.$parentConfigurator) as T);
        this.handleRuleError(ruleContainer.name + " " + ruleContainer.$type, res);
        return res;
    }


    public runRuleTypeAsync<T extends IConfiguratorRuleArgs | ISceneRuleArgs>(
        config: Configurator,
        ruleType: string,
        args?: T
    ): ng.IPromise<IJsResult<T>> {
        return this.args.ruleService.runRuleTypeAsync(config, ruleType, args || this.args.getRuleArgs(config) as T).then(res => {
            this.handleRuleError(ruleType, res);
            return res;
        });
    }

    public runActionByIdAsync<T extends IConfiguratorRuleArgs | ISceneRuleArgs>(
        config: Configurator,
        actionId: string,
        args?: T
    ): ng.IPromise<IJsResult<T>> {
        return this.args.ruleService.runActionRuleByIdAsync(config as Configurator, actionId, args || this.args.getRuleArgs(config) as T)
        .then(res => {
            this.handleRuleError(actionId, res);
            return res;
        });
    }

    public runOptionFilterSourceRule() {
        
    }
   

    public autoCompleteQuery(query: string, field: Field) {
        if (query) {
            if (field.autoCompleteSource == eAutoCompleteSource.table) {
                // return this.tablesPromise.then(() => {
                let arr = this.tableArraysDb[field.idTable].data;
                let results = SearchUtils.search(
                    query, arr,
                    item => item[Utils.scrubName(field.labelColumn || field.valueColumn)]);
                let scrubValueCol = Utils.scrubName(field.valueColumn);
                let scrubLabelCol = Utils.scrubName(field.labelColumn);
                let scrubImageCol = Utils.scrubName(field.imageColumn);
                // map the results to options
                let options = results.map(item => {
                    let o = new Option();
                    o.value = item[scrubValueCol];
                    o.label = item[scrubLabelCol];
                    o.image = item[scrubImageCol];
                    return o;
                });
                return this.args.$q.when(options);
                // });
            } else if (field.autoCompleteSource == eAutoCompleteSource.database) {
                return this.args.api.products.autocomplete(
                    field.$parentConfigurator.idProduct, field.id, query, this.args.tracker);
            }
        }
        return this.args.$q.when([]);
    }

    public autoCompleteGetLabel(value: any, field: Field) {
        if (field.labelColumn) {
            // when autocomplete fields are using the labelColumn, then when they are first opening,
            // they will request for the label of their current value
            if (field.autoCompleteSource == eAutoCompleteSource.table) {
                let scrubValueCol = Utils.scrubName(field.valueColumn);
                let scrubLabelCol = Utils.scrubName(field.labelColumn);
                let scrubImageCol = Utils.scrubName(field.imageColumn);

                // return this.tablesPromise.then(() => {
                let arr = this.tableArraysDb[field.idTable].data;
                let row = arr.find(item => item[scrubValueCol] === value);
                return row ? row[scrubLabelCol] : null;
                // });
            } else if (field.autoCompleteSource == eAutoCompleteSource.database) {

            }
        }

        return value;
    }

    public getOptionFilterSource(filter: OptionFilter): any[] {
        let arr: any[] = [];
        if (filter.source == eOptionFilterSource.table) {
            let tableObject = this.tableArraysDb[filter.idTable];
            if (tableObject) arr = tableObject.data;
        } else if (filter.source == eOptionFilterSource.optionFilter) {
            let f = filter.$parentConfigurator.$manager.get(filter.idOptionFilter) as OptionFilter;
            arr = f ? f.$source : [];
        } else { //must be a query
            // get the source table by running the query
            let ruleArgs: IOptionFilterRuleArgs = this.args.getRuleArgs(filter.$parentConfigurator);
            arr = this.runRuleContainerSync(filter, ruleArgs).parameters.source;
        }
        if (arr == null) throw new Error(`Could not find source for option filter '${filter.name}'`);
        filter.$source = arr;
        return arr;
    }

    public shouldRunOptionFilter(config: Configurator, filter: OptionFilter) {
        let shouldRun = true;
        if (config.v >= 1 && filter.optimized) {  // older configurators will not have the needed info for optimization
            let last = filter.$lastValues;
            if (last) { // if it has lastReferenceValues then it has already been run
                shouldRun = Object.keys(last).some(fieldId => {
                    let field = config.$manager.get<Field>(fieldId);
                    if (field.isArrayType()) {
                        return !(field.value as any[]).equals(last[fieldId]);
                    } else {
                        return (last[fieldId] != field.value);
                    }
                });
            }
        }
        // if this filter is sourced from another option filter, we need to also check if that option filter
        // needed to be run in this cycle
        if (filter.source == eOptionFilterSource.optionFilter ||
            (filter.source == eOptionFilterSource.query && filter.queryMode == eQueryMode.optionFilter)
        ) {
            let otherFilter = config.$manager.get(filter.idOptionFilter) as OptionFilter;
            shouldRun = otherFilter.$shouldRun || shouldRun;
        } else if (filter.source == eOptionFilterSource.query && filter.queryMode == eQueryMode.parentOptionFilter) {
            let otherFilter = config.$parentConfigurator.$manager.get(filter.idOptionFilter) as OptionFilter;
            shouldRun = otherFilter.$shouldRun || shouldRun;
        }
        filter.$shouldRun = shouldRun;
        return shouldRun;
    }

    public runOptionFilter(config: Configurator, s: IOptionFilterInfo) {
        let filter = config.$manager.get<OptionFilter>(s.filterId);
        // optimize by not running if all the inputs are the same as last time it was run
        let shouldRun = this.shouldRunOptionFilter(config, filter);

        if (shouldRun) {
            this._optionFilterRunCount++;

            let source = this.getOptionFilterSource(filter);
            let clientLang = this.args.$rootScope.clientLanguage;
            let shouldTranslate = (clientLang != this.args.$rootScope.companySettings.defaultLanguage);

            // start with full table. //Use int array and preallocate it for performance reasons
            let matchedRows: number[] = [];
            for (let r = 0; r < source.length; r++) {
                matchedRows.push(r);
            }

            for (let f = 0; f < s.fieldIds.length; f++) {
                // get the real field from this configurator
                let field = config.$manager.get<Field>(s.fieldIds[f]);
                let fieldBaseType = field.getBaseType();
                let fieldIsArray = field.isArrayType();

                let newOptions: IOption[] = [];
                let scrubValueCol = Utils.scrubName(field.valueColumn);
                let scrubLabelCol = Utils.scrubName(field.labelColumn);
                let scrubSortCol = Utils.scrubName(field.sortColumn);
                let scrubTranslateCol = shouldTranslate ? 
                    (scrubLabelCol ?
                        scrubLabelCol + clientLang.toUpperCase()
                        : scrubValueCol + clientLang.toUpperCase())
                    : "";
                let scrubImageCol = Utils.scrubName(field.imageColumn);
                let scrubImageWhenSelectedCol = Utils.scrubName(field.imageWhenSelectedColumn);
                let scrubDescCol = Utils.scrubName(field.descriptionColumn);
                var scrubDescTranslateCol = shouldTranslate ? (scrubDescCol + clientLang.toUpperCase()) : "";

                // fill in options... we only want unique options
                let uniqueDb: { [value: string]: IOption } = {};
                for (let rowIndex of matchedRows) {
                    let row = source[rowIndex];
                    if (!uniqueDb[row[scrubValueCol]]) {
                        let entry = {
                            label: row.hasOwnProperty(scrubTranslateCol) ? row[scrubTranslateCol] : row[scrubLabelCol],
                            image: row[scrubImageCol],
                            imageWhenSelected: row[scrubImageWhenSelectedCol],
                            description: row.hasOwnProperty(scrubDescTranslateCol) ? row[scrubDescTranslateCol] : row[scrubDescCol],
                            value: Field.convertToType(row[scrubValueCol], fieldBaseType, field.precision),
                            sort: row[scrubSortCol]
                        } as IOption;
                        uniqueDb[row[scrubValueCol]] = entry;
                        newOptions.push(entry)
                    }                    
                }

                // sort if applicable
                let sortCol = field.sortColumn ? "sort" : (field.labelColumn ? "label" : "value");
                if (field.sort == eSort.ascending) {
                    newOptions.sortBy(o => o[sortCol]);
                } else if (field.sort == eSort.descending) {
                    newOptions.sortByDescending(o => o[sortCol]);
                }

                // set the new options all at once
                field.options = newOptions;

                this.handleSingleOption(field, filter);

                // now recalculate the matched rows for the next field
                if (f !== s.fieldIds.length - 1) {
                    matchedRows = matchedRows.filter(rowIndex => {
                        let cellValue = source[rowIndex][scrubValueCol];
                        cellValue = Field.convertToType(cellValue, fieldBaseType, field.precision);
                        return fieldIsArray ? (field.value as any[]).contains(cellValue) : (cellValue == field.value);
                    });
                }
            }

            this.storeLastValuesFor(config, s);
        }
    }

    public handleSingleOption(field: Field, optionFilter: OptionFilter) {
        // if there is only one option left, check the specified behavior on the option filter
        if (optionFilter.singleOptionBehavior == eSingleOptionBehavior.disable) {
            field.enabled = (field.options.length > 1);
        } else if (optionFilter.singleOptionBehavior == eSingleOptionBehavior.hide) {
            field.visible = (field.options.length > 1);
        }
    }

    private _optionFilterRunCount = 0;

    protected storeLastValuesFor(config: Configurator, s: IOptionFilterInfo) {
        if (config.v >= 1) { // older configurators will not have the needed info for optimization
            // store last field values for optimization
            let filter = config.$manager.get<OptionFilter>(s.filterId);
            let last = filter.$lastValues = filter.$lastValues || {};
            filter.fieldReferences.forEach(fieldId => {
                let field = config.$manager.get<Field>(fieldId);
                if (field.isArrayType()) {
                    last[fieldId] = (field.value as any[]).map(v => v);
                } else {
                    last[fieldId] = field.value;
                }
            });
            s.fieldIds.forEach(fieldId => {
                let field = config.$manager.get<Field>(fieldId);
                if (field.isArrayType()) {
                    last[field.id] = (field.value as any[]).map(v => v);
                } else {
                    last[field.id] = field.value;
                }
            });
        }
    }

    public runSelects(config: Configurator): ng.IPromise<any> {
        let promise: ng.IPromise<any> = (new KPromise(null)) as any;
        if (!config.optionFilters.length) return promise; //shortcircuit if there is nothing to do here

        config.$freezeFieldValueCalculation = true;  // freeze field value calculation for perf

        let startTime = Date.now();
        this._optionFilterRunCount = 0;
        // fill in the db
        if (!this.odb) this.odb = {};

        if (!this.odb[config.idProduct]) {
            this.odb[config.idProduct] = {};
            this.filterLevelDb[config.idProduct] = {};
            // loop through each select table, find all registered fields
            let allFields = config.getFields(f => f.canJoinOptionFilter() && f.selectSource == eSelectSource.optionFilter);
            config.optionFilters.forEach(o => {
                let info: IOptionFilterInfo = {
                    filterId: o.id,
                    fieldIds: allFields.filter(f => f.idOptionFilter == o.id).map(f => f.id)
                };

                this.odb[config.idProduct][o.id] = info;
            });

            // filters are organized by level of dependencies.  Those at a level of 1 have no dependencies
            // so we just roll through the filters, and make a jagged array where the primary index is 
            // the level, and we loop them and run them
            config.optionFilters.forEach(filter => {
                let arr = this.filterLevelDb[config.idProduct][filter.level];
                if (!arr) this.filterLevelDb[config.idProduct][filter.level] = [];
                this.filterLevelDb[config.idProduct][filter.level].push(filter.id);
            });
            //// fill in start filters also
            // this.startFilters = config.optionFilters.filter(f => !f.dependsOn || f.dependsOn.length == 0);
        }

        // if any of the option filters in the configurator are sourced by a database, then we just have the server
        // do all the figuring
        if (!config.filtersShouldRunOnClient()) {
            /* another level of optimization... if all the fields referenced by option filters in their query 
            rule and fields in the option filter haven't changed value, then there is no reason to call the server */
            let shouldRun = true;
            if (config.v >= 1) { // older configurators will not have the needed info for optimization
                for (let filter of config.optionFilters) {
                    shouldRun = this.shouldRunOptionFilter(config, filter);
                    if (shouldRun) break;
                }
            }

            if (shouldRun) {
                let fieldOptions: IOptionFilterField[] = [];
                config.optionFilters.forEach(filter => {
                    this.odb[config.idProduct][filter.id].fieldIds.forEach(fieldId => {
                        let field = config.$manager.get<Field>(fieldId);
                        fieldOptions.push({ id: field.id, name: field.name, options: field.options });
                    });
                });
                let lastValues = {};
                config.optionFilters.forEach(filter => {
                    lastValues[filter.id] = filter.$lastValues;
                });
                let runArgs = {
                    configuredProduct: config.getConfiguredProduct(true),
                    lastValues,
                    fieldOptions
                } as IOptionFilterRunArgs;
                promise = this.args.api.optionFilters.run(runArgs, this.args.tracker).then(r => {
                    r.filters.forEach(filterResult => {
                        filterResult.fields.forEach(f => {
                            let field = config.$manager.get<Field>(f.id);
                            let filter = config.$manager.get<OptionFilter>(filterResult.id);
                            // sort if applicable
                            let sortCol = field.sortColumn ? "sort" : (field.labelColumn ? "label" : "value");
                            if (field.sort == eSort.ascending) {
                                f.options.sortBy(o => o[sortCol]);
                            } else if (field.sort == eSort.descending) {
                                f.options.sortByDescending(o => o[sortCol]);
                            }

                            field.options = f.options;

                            this.handleSingleOption(field, filter);
                        });
                    });

                    // store optimization info
                    r.filters.forEach(filter => this.storeLastValuesFor(config, this.odb[config.idProduct][filter.id]));
                });
            }
        } else {
            /* we have all tables, and no databases, so we operate locally to avoid a roundtrip to the server
            because getTables is a KPromise that isn't a real promise, we can run these as if they were 
            sync (because they are) */
            let level = 1;
            while (true) {
                let filterIds = this.filterLevelDb[config.idProduct][level];
                if (filterIds) {
                    filterIds.forEach(filterId => {
                        this.runOptionFilter(config, this.odb[config.idProduct][filterId]);
                    });
                    level++;
                } else {
                    break;
                }
            }

            promise = (new KPromise(null)).then(() => {
                // store optimization info
                config.optionFilters.forEach(filter => this.storeLastValuesFor(config, this.odb[config.idProduct][filter.id]));
            }) as any;
        }

        return promise.then(() => {
            config.$freezeFieldValueCalculation = false;
            Logger.logRuleEnd(config.name, `option filters (${this._optionFilterRunCount})`, startTime);
        });
    }
}
